import { Form } from 'antd';
import { get } from 'lodash';
import React, { memo, useEffect } from 'react';
import { isRealHts } from '@xbcb/entry-utils/dist/lib/htsUtil';
import { possibleZeroValueHts } from '@xbcb/entry-utils/dist/lib/possibleZeroValueHts';
import { LabelAbove } from '@xbcb/static-text-components';
import AddUnitAssistButton from 'components/AddUnitAssistButton';
import CurrencyField from 'components/CurrencyField';
import LineItemTariffPga from 'components/LineItemTariffPga';
import LineItemTariffUnitQuantities from 'components/LineItemTariffUnitQuantities';
import TrumpListTooltip from 'components/TrumpListTooltip';
import UnitAssistInput from 'components/UnitAssistInput';
import { useHtsValues } from 'context/htsValues';
import {
  getRealTariffIndex,
  setProductUnitValueField,
} from 'libs/commercialInvoiceProductHelpers';
import dutyComputation from 'libs/dutyDisplay';
import { useWorkOrderTaskConfiguration } from 'libs/hooks';
import { isReadOnly, shouldRequire } from 'libs/readWriteControl';
import { isUOMEditAllowedForHts } from 'libs/uomDefaults';
import { useTheme } from 'styled-components';
import { CommercialInvoiceBaseProps, ValueField } from 'types';
import {
  StyledAddAssistButtonDiv,
  StyledFormInlineDiv,
  StyledHtsDutyParagraph,
  StyledHtsInfoDiv,
  StyledTariffUnitValueWrapperDiv,
  StyledTextParagraph,
} from './styles';

export interface LineItemTariffTabProps extends CommercialInvoiceBaseProps {
  index: number;
  originCountryCode?: string;
  setCvTabManuallyAdded: (show: boolean) => void;
  setAdTabManuallyAdded: (show: boolean) => void;
  adTabVisible: boolean;
  cvTabVisible: boolean;
}

/**
 * LineItemTariff
 *
 * The Tariff tab for line items.
 */

export const LineItemTariffTab: React.FC<LineItemTariffTabProps> = ({
  fullNamePath,
  localNamePath,
  workOrderType,
  disabled,
  readOnly,
  required,
  index,
  originCountryCode,
  setAdTabManuallyAdded,
  setCvTabManuallyAdded,
  adTabVisible,
  cvTabVisible,
}: LineItemTariffTabProps) => {
  const theme = useTheme();
  const { readWriteControlsConfig } = useWorkOrderTaskConfiguration();
  const form = Form.useFormInstance();

  const fullProductNamePath = [...fullNamePath.slice(0, 4)];
  const fullCurrencyNamePath = [...fullNamePath.slice(0, 2), 'currency'];
  const fullUnitValueNamePath = [...fullNamePath, 'unitValue'];
  const unitValueNamePath = [...localNamePath, 'unitValue'];
  const fullUnitAssistNamePath = [...fullNamePath, 'unitAssist'];
  const unitAssistNamePath = [...localNamePath, 'unitAssist'];

  const { htsValues } = useHtsValues();
  const htsNumber = htsValues.htsNumbers[index] || 'Tariff Info';
  const htsObj = htsValues.htsObjects[index];

  // We should only show unit values/quantities for only one HTS (in multiple HTS
  // cases there should be only one real HTS)
  const isOnlyOrRealHts = () =>
    !get(htsObj, 'additionalHTS') || isRealHts(htsNumber);

  Form.useWatch(fullUnitValueNamePath);
  const unitAssist = Form.useWatch(fullUnitAssistNamePath, {
    form,
    preserve: true,
  });
  const hasAssist = () => !!unitAssist?.value || !!unitAssist?.currency;

  const isWorkOrderType = !!workOrderType;

  let currencySelectProps = {};
  if (isWorkOrderType) {
    currencySelectProps = {
      currencyNamePath: fullCurrencyNamePath,
    };
  } else {
    currencySelectProps = { currency: 'USD' };
  }

  // Pre-fill unit value fields based on product value/quantity
  useEffect(() => {
    if (isRealHts(htsNumber)) {
      const realTariffIndex = getRealTariffIndex(form, [
        ...fullProductNamePath,
        'lines',
      ]);

      if (realTariffIndex !== undefined && realTariffIndex === index) {
        if (!form.getFieldValue([...fullUnitValueNamePath, 'value'])) {
          setProductUnitValueField({
            form,
            unitField: ValueField.UNIT_VALUE,
            totalField: ValueField.TOTAL_VALUE,
            productNamePath: fullProductNamePath,
            currencyNamePath: fullCurrencyNamePath,
            realTariffIndex,
            shouldSetCurrency: false,
          });
        }
        if (!form.getFieldValue([...fullUnitAssistNamePath, 'value'])) {
          setProductUnitValueField({
            form,
            unitField: ValueField.UNIT_ASSIST,
            totalField: ValueField.TOTAL_ASSIST,
            productNamePath: fullProductNamePath,
            currencyNamePath: fullCurrencyNamePath,
            realTariffIndex,
            shouldSetCurrency: false,
          });
        }
      }
    }
  }, [htsNumber, index]);

  return (
    <div>
      {htsObj && (
        <StyledHtsInfoDiv>
          <StyledTextParagraph>{htsObj.text}</StyledTextParagraph>
          <div>
            <StyledHtsDutyParagraph>
              Duty:{' '}
              {dutyComputation({
                htsNumber,
                ...htsObj,
              })}
            </StyledHtsDutyParagraph>
            {htsObj.trumpList &&
              (!originCountryCode || originCountryCode === 'CN') && (
                <TrumpListTooltip list={htsObj.trumpList} />
              )}
          </div>
          {htsObj.endDate && <p>End Date: {htsObj.endDate}</p>}
        </StyledHtsInfoDiv>
      )}
      {isOnlyOrRealHts() && (
        <>
          <StyledFormInlineDiv>
            <StyledTariffUnitValueWrapperDiv>
              <LabelAbove text="Unit Value" />
              <CurrencyField
                form={form}
                {...currencySelectProps}
                debounce={true}
                fullNamePath={fullUnitValueNamePath}
                localNamePath={unitValueNamePath}
                $itemSize={theme.size.tiny_short}
                precision={6}
                required={shouldRequire(
                  'invoices.products.lines.tariffs.unitvalue',
                  readWriteControlsConfig,
                  required,
                )}
                max={100000000}
                readOnly={
                  !isUOMEditAllowedForHts(htsNumber) ||
                  isReadOnly(
                    'invoices.products.lines.tariffs.unitvalue',
                    readWriteControlsConfig,
                  )
                }
                disabled={!isUOMEditAllowedForHts(htsNumber)}
                min={possibleZeroValueHts(htsNumber) ? 0.0 : undefined}
              />
            </StyledTariffUnitValueWrapperDiv>
            {hasAssist() && (
              <StyledTariffUnitValueWrapperDiv>
                <UnitAssistInput
                  form={form}
                  {...currencySelectProps}
                  debounce={true}
                  fullNamePath={fullUnitAssistNamePath}
                  localNamePath={unitAssistNamePath}
                  $itemSize={theme.size.tiny_short}
                  precision={4}
                  required={
                    required ||
                    !!readWriteControlsConfig[
                      'invoices.products.lines.tariffs.unitassist'
                    ]?.required
                  }
                  max={100000000}
                  readOnly={
                    readOnly ||
                    !!readWriteControlsConfig[
                      'invoices.products.lines.tariffs.unitassist'
                    ]?.readOnly
                  }
                  min={0.0}
                  disabled={disabled}
                />
              </StyledTariffUnitValueWrapperDiv>
            )}
            <LineItemTariffUnitQuantities
              fullNamePath={[...fullNamePath]}
              localNamePath={[...localNamePath]}
              index={index}
              readOnly={readOnly}
              disabled={disabled}
              required={required}
            />
            <StyledAddAssistButtonDiv>
              {!hasAssist() && (
                <AddUnitAssistButton
                  form={form}
                  fullNamePath={fullUnitAssistNamePath}
                  disabled={disabled}
                  readOnly={readOnly}
                />
              )}
            </StyledAddAssistButtonDiv>
          </StyledFormInlineDiv>
          <LineItemTariffPga
            fullNamePath={[...fullNamePath]}
            localNamePath={[...localNamePath]}
            workOrderType={workOrderType}
            index={index}
            readOnly={readOnly}
            disabled={disabled}
            required={required}
            setAdTabManuallyAdded={setAdTabManuallyAdded}
            setCvTabManuallyAdded={setCvTabManuallyAdded}
            adTabVisible={adTabVisible}
            cvTabVisible={cvTabVisible}
          />
        </>
      )}
      {!isOnlyOrRealHts() && (
        <>
          <StyledFormInlineDiv>
            <LineItemTariffUnitQuantities
              fullNamePath={[...fullNamePath]}
              localNamePath={[...localNamePath]}
              index={index}
              readOnly={readOnly}
              required={required}
            />
          </StyledFormInlineDiv>
        </>
      )}
    </div>
  );
};

export default memo(LineItemTariffTab);
